import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z as zod } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { updateUserAccountRole } from '@/api/accounts';

const schema = zod.object({
  role_name: zod.string().min(1, { message: 'Role is required' }),
});

type Values = zod.infer<typeof schema>;

interface EditAccountRoleModalProps {
  accountId: string;
  userId: string;
  currentRole: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const accountRoles = [
  { value: 'account_owner', label: 'Account Owner' },
  { value: 'account_admin', label: 'Account Admin' },
  { value: 'account_manager', label: 'Account Manager' },
];

export function EditAccountRoleModal({
  accountId,
  userId,
  currentRole,
  open,
  onClose,
  onSuccess,
}: EditAccountRoleModalProps): React.JSX.Element {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      role_name: currentRole,
    },
  });

  React.useEffect(() => {
    if (currentRole) {
      reset({ role_name: currentRole });
    }
  }, [currentRole, reset]);

  const mutation = useMutation({
    mutationFn: (data: Values) => updateUserAccountRole(accountId, userId, data),
    onSuccess: () => {
      toast.success('Account role updated successfully');
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update account role');
    },
  });

  const onSubmit = React.useCallback(
    async (values: Values): Promise<void> => {
      mutation.mutate(values);
    },
    [mutation]
  );

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={open}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(onSubmit),
      }}
    >
      <DialogTitle>Edit Account Role</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Controller
            control={control}
            name="role_name"
            render={({ field }) => (
              <FormControl error={Boolean(errors.role_name)} fullWidth>
                <InputLabel>Account Role</InputLabel>
                <Select {...field} label="Account Role">
                  {accountRoles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.role_name && (
                  <FormHelperText>{errors.role_name.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button disabled={mutation.isPending} type="submit" variant="contained">
          {mutation.isPending ? 'Updating...' : 'Update Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
